import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, Search, FileText, Filter, X, ArrowUpDown, Eye, EyeOff,
  MoreHorizontal, Pencil, Trash2, Copy, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProgrammesPage = () => {
  const navigate = useNavigate();
  const [programmes, setProgrammes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPublished, setFilterPublished] = useState('');
  const [activeTab, setActiveTab] = useState('programmes');

  const fetchProgrammes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterCategory) params.set('category', filterCategory);
      if (filterPublished) params.set('published', filterPublished);
      const res = await fetch(`${API}/programmes?${params}`, { credentials: 'include' });
      if (res.ok) setProgrammes(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, filterCategory, filterPublished]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API}/programmes/categories`, { credentials: 'include' });
      if (res.ok) setCategories(await res.json());
    } catch (e) {}
  }, []);

  useEffect(() => { fetchProgrammes(); fetchCategories(); }, [fetchProgrammes, fetchCategories]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce programme ?')) return;
    try {
      const res = await fetch(`${API}/programmes/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Programme supprimé'); fetchProgrammes(); }
    } catch (e) { toast.error('Erreur'); }
  };

  const clearFilters = () => {
    setSearch('');
    setFilterCategory('');
    setFilterPublished('');
  };

  const hasFilters = search || filterCategory || filterPublished;
  const activeFilterCount = [search, filterCategory, filterPublished].filter(Boolean).length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="programmes-page">
        {/* Breadcrumb */}
        <div className="text-sm text-slate-500">
          <span>Bibliothèque</span> <span className="mx-1">&gt;</span>
          <span className="text-teal-600 font-medium">Programmes</span>
        </div>

        <h1 className="text-3xl font-bold text-slate-900">Bibliothèque de programmes</h1>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 dark:bg-slate-800 w-full justify-start">
            <TabsTrigger value="programmes" data-testid="tab-programmes">Programmes</TabsTrigger>
            <TabsTrigger value="tableau" data-testid="tab-tableau">Tableau de programmes</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Catégories</TabsTrigger>
          </TabsList>

          <TabsContent value="programmes" className="space-y-4 mt-4">
            {/* Create button */}
            <Button
              className="bg-teal-500 hover:bg-teal-600 text-white"
              onClick={() => navigate('/bibliotheque/programmes/nouveau')}
              data-testid="create-programme-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Créer un programme
            </Button>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative w-48">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-9 text-sm"
                  data-testid="search-programmes"
                />
              </div>

              {activeFilterCount > 0 && (
                <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                  <ArrowUpDown className="h-3 w-3 mr-1" />Trier {activeFilterCount}
                </Badge>
              )}

              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-44 h-9 text-sm" data-testid="filter-category">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_cats">Toutes</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterPublished} onValueChange={setFilterPublished}>
                <SelectTrigger className="w-44 h-9 text-sm" data-testid="filter-published">
                  <SelectValue placeholder="Visibilité en ligne" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_vis">Toutes</SelectItem>
                  <SelectItem value="true">Publiés</SelectItem>
                  <SelectItem value="false">Non publiés</SelectItem>
                </SelectContent>
              </Select>

              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 text-sm h-9">
                  <X className="h-3 w-3 mr-1" /> Effacer tout
                </Button>
              )}
            </div>

            {/* Programme List */}
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div>
            ) : programmes.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Aucun programme</h3>
                  <p className="text-slate-500 mb-4">Créez votre premier programme de formation</p>
                  <Button className="bg-teal-500 hover:bg-teal-600 text-white" onClick={() => navigate('/bibliotheque/programmes/nouveau')}>
                    <Plus className="h-4 w-4 mr-2" />Créer un programme
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                {programmes.map((prog) => (
                  <div
                    key={prog.programme_id}
                    className="flex items-center px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors group"
                    onClick={() => navigate(`/bibliotheque/programmes/${prog.programme_id}`)}
                    data-testid={`prog-row-${prog.programme_id}`}
                  >
                    {/* Colored dot */}
                    <div
                      className="w-4 h-4 rounded-full shrink-0 mr-4"
                      style={{ backgroundColor: prog.color || '#94a3b8' }}
                    />
                    {/* Title */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                        {prog.title}
                      </p>
                    </div>
                    {/* Category badge */}
                    {prog.category && (
                      <Badge variant="outline" className="text-xs mr-4 hidden sm:inline-flex">{prog.category}</Badge>
                    )}
                    {/* Code */}
                    <span className="text-sm text-slate-400 font-mono mr-4 shrink-0">
                      {prog.internal_code || prog.programme_id}
                    </span>
                    {/* Visibility */}
                    <span className="mr-4 shrink-0">
                      {prog.published_online
                        ? <Eye className="h-4 w-4 text-emerald-500" />
                        : <EyeOff className="h-4 w-4 text-slate-300" />
                      }
                    </span>
                    {/* Variants */}
                    {prog.variants_count > 0 && (
                      <span className="text-xs text-slate-500 mr-4 shrink-0">{prog.variants_count} variantes</span>
                    )}
                    {/* Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); navigate(`/bibliotheque/programmes/${prog.programme_id}`); }}
                        data-testid={`edit-prog-${prog.programme_id}`}
                      >
                        <Pencil className="h-4 w-4 text-slate-400" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); handleDelete(prog.programme_id); }}
                        data-testid={`delete-prog-${prog.programme_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="tableau" className="mt-4">
            <Card><CardContent className="py-12 text-center text-slate-500">
              Tableau de programmes — Vue synthétique à venir
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="categories" className="mt-4">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Catégories de programmes</h3>
                {categories.length === 0 ? (
                  <p className="text-slate-500">Aucune catégorie créée. Les catégories apparaîtront automatiquement lorsque vous créerez des programmes.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {categories.map(c => (
                      <Badge key={c} variant="outline" className="text-sm py-1 px-3">{c}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ProgrammesPage;
